/**
 * @fileoverview Helper functions and constants for admission model operations.
 */

import {intersection, includes} from "lodash";
import {
  Template,
  Values,
  blockIsVisible,
  computeChildrenBlock,
  isBranchNode,
  isResidentExperienceContextNode,
  isSlideNode,
  isTableOfContentNode,
  ResidentExperienceTemplate,
} from "@reside/forms";
import {Admission} from "@reside/reside-api-admission";

import {isFileReference} from "../utils/helpers/questions";
import {AdmissionAnswerObjects} from "../services/AdmissionsService";
import {getPreflightedAnswers} from "./AdmissionsModel.helpers";

/**
 * Enum representing the different representative roles.
 */
export enum RepresentativeRoles {
  REPRESENTATIVE = "REPRESENTATIVE",
  RESIDENT = "RESIDENT",
}

/**
 * Well-known answer IDs for form fields.
 * TODO: Consider moving these to a constants file.
 */
export const REPRESENTATIVE_ROLE = "representative_role";
export const RESIDENT_FIRST_NAME = "resident_firstName";
export const RESIDENT_LAST_NAME = "resident_lastName";
export const RESIDENT_PCC_ACCOUNT_STATUS = "resident_pccAccountStatus";
export const RESIDENT_DATE_OF_BIRTH = "resident_dateOfBirth";
export const REPRESENTATIVE_FIRST_NAME = "representative_firstName";
export const REPRESENTATIVE_LAST_NAME = "representative_lastName";

/**
 * Constants for signature-related fields.
 */
export const MAIN_SIGNATURE = "mainSignature";
export const MAIN_SIGNATURE_DATE = "c896825d-a7eb-4772-8d57-a612d5a0311d";

/**
 * A node that points at a question. ``@reside/forms`` has no runtime guard for form controls, so
 * the reference is read off the node directly; recursion goes through its ``isBranchNode``.
 */
type ReferencingNode = Readonly<{reference?: string}>;

const referencesMainSignature = (nodes?: ReadonlyArray<unknown>): boolean =>
  (nodes ?? []).some(
    node =>
      (node as ReferencingNode)?.reference === MAIN_SIGNATURE ||
      (isBranchNode(node) && referencesMainSignature(node.children)),
  );

/**
 * The slide holding the main signature field, or undefined when the deck has none.
 *
 * Every workflow has its own deck, so neither the slide's position nor its id is stable across
 * them. It is located by the form control that references the signature.
 */
export const findMainSignatureSlide = <T extends {children?: ReadonlyArray<any>}>(
  slides?: ReadonlyArray<T>,
): T | undefined =>
  (slides ?? []).find(slide => referencesMainSignature(slide.children));

/**
 * Index of the slide holding the main signature field, or -1 when the deck has none.
 */
export const findMainSignatureSlideIndex = <
  T extends {children?: ReadonlyArray<any>}
>(
  slides?: ReadonlyArray<T>,
): number => {
  const slide = findMainSignatureSlide(slides);

  return slide ? (slides ?? []).indexOf(slide) : -1;
};

/**
 * Determines if signatures should be cleared when representative role changes.
 *
 * @param {Values} values Current form values.
 * @param {Values} newValues New form values.
 * @returns {boolean} True if signatures should be cleared.
 */
export const shouldClearSignatures = (values: Values, newValues: Values) => {
  const prevRole = values[REPRESENTATIVE_ROLE];
  const nextRole = (newValues ?? {})[REPRESENTATIVE_ROLE];

  if (!prevRole || !nextRole) {
    return false;
  }

  return prevRole !== nextRole;
};

/**
 * Calculates the print name based on the representative role.
 *
 * @param {Values} values Form values containing name and role information.
 * @returns {string} The appropriate name to display for printing.
 */
export const calculatePrintName = (values: Values) => {
  const role = values[REPRESENTATIVE_ROLE];

  const residentName = `${values[RESIDENT_FIRST_NAME]} ${values[RESIDENT_LAST_NAME]}`;
  const representativeName = `${values[REPRESENTATIVE_FIRST_NAME]} ${values[REPRESENTATIVE_LAST_NAME]}`;

  return role === RepresentativeRoles.RESIDENT
    ? residentName
    : representativeName;
};

/**
 * Gets the question type and file reference status for a given key.
 *
 * @param {string} key The question key to look up.
 * @param {Template} template The template containing question references.
 * @returns {Object} Object containing question type and file reference status.
 * @returns {string} returns.questionType The type of the question.
 * @returns {boolean} returns.fileReference Whether the question is a file reference.
 */
export const getQuestionType = (key: string, template: Template) => {
  if (Object.prototype.hasOwnProperty.call(template.references, key)) {
    const type = template.references[key].type;

    return {
      questionType: type,
      fileReference: isFileReference(type),
    };
  }

  return {};
};

/**
 * Checks if a question is pre-flighted in the template.
 *
 * @param {string} key The question key to check.
 * @param {Template} template The template containing question references.
 * @returns {boolean} True if the question is pre-flighted.
 */
export const getPreFlighted = (key: string, template: Template) => {
  if (!Object.prototype.hasOwnProperty.call(template.references, key)) {
    return false;
  }

  return Boolean(template.references[key]?.preFlighted);
};

/**
 * Converts form values to admission answer objects with metadata.
 *
 * @param {Object} params The function parameters.
 * @param {Values} params.values Form values to convert.
 * @param {AdmissionAnswerObjects} params.admissionAnswersObjects Existing answer objects.
 * @param {string} params.modifier The modifier for the answers.
 * @param {number} [params.updated] Timestamp for when answers were updated.
 * @param {Template} params.template Template containing question references.
 * @returns {AdmissionAnswerObjects} Converted answer objects with metadata.
 */
export const getAnswersFromFormValues = ({
  values,
  admissionAnswersObjects,
  modifier,
  updated = Math.floor(Date.now() / 1000),
  template,
}: {
  values: Values;
  admissionAnswersObjects: AdmissionAnswerObjects;
  modifier: string;
  updated: number;
  template: Template;
}): AdmissionAnswerObjects =>
  Object.entries(values).reduce((answers, [key, value]) => {
    const hasAnswerObject = Object.prototype.hasOwnProperty.call(
      admissionAnswersObjects,
      key,
    );

    const hasRelevantValue =
      value !== "" && value !== undefined && value !== null;

    if (!hasAnswerObject && !hasRelevantValue) {
      return answers;
    }

    return {
      ...answers,
      [key]: {
        ...(admissionAnswersObjects[key] ?? {}),
        ...getQuestionType(key, template),
        preFlight: getPreFlighted(key, template),
        questionId: key,
        value,
        metadata: {
          modifier,
          updated,
        },
      },
    };
  }, {});

/**
 * Updates the list of completed slides based on visibility and signature changes.
 *
 * @param {Object} params The function parameters.
 * @param {Values} params.answers Current form answers.
 * @param {Values} params.values New form values.
 * @param {ReadonlyArray<string>} params.visibleSlideIds Currently visible slide IDs.
 * @param {ReadonlyArray<string>} params.completedVisibleSlideIds Previously completed visible slide IDs.
 * @returns {ReadonlyArray<string>} Updated list of completed slide IDs.
 */
export function updateCompletedSlides({
  answers,
  values,
  visibleSlideIds,
  completedVisibleSlideIds,
  slides,
}: {
  answers: Values;
  values: Values;
  visibleSlideIds: ReadonlyArray<string>;
  completedVisibleSlideIds: ReadonlyArray<string>;
  slides?: ReadonlyArray<{id: string; children?: ReadonlyArray<unknown>}>;
}) {
  //This is just check to prevent having completedVisibleSlides different than actual available visibleSlides (e.g. can happen after changing of PayerSource)
  const updatedCompletedSlides = intersection(
    completedVisibleSlideIds,
    visibleSlideIds,
  );

  if (!shouldClearSignatures(answers, values)) {
    return updatedCompletedSlides;
  }

  const signatureSlideId = findMainSignatureSlide(slides)?.id;

  if (!signatureSlideId) {
    return updatedCompletedSlides;
  }

  return updatedCompletedSlides.filter(slideId => slideId !== signatureSlideId);
}

/**
 * Updates the last viewed slide index based on visibility.
 *
 * @param {Object} params The function parameters.
 * @param {number} params.lastViewedSlideIdx Current last viewed slide index.
 * @param {ReadonlyArray<string>} params.visibleSlideIds Currently visible slide IDs.
 * @param {ReadonlyArray<string>} params.allSlideIds All available slide IDs.
 * @returns {number} Updated last viewed slide index.
 */
export function updateLastViewedSlideIdx({
  lastViewedSlideIdx,
  visibleSlideIds,
  allSlideIds,
}: {
  lastViewedSlideIdx: number;
  visibleSlideIds: ReadonlyArray<string>;
  allSlideIds: ReadonlyArray<string>;
}) {
  //If LastSlide is not visible reset to first slide
  return includes(visibleSlideIds, allSlideIds[lastViewedSlideIdx])
    ? lastViewedSlideIdx
    : 0;
}

/**
 * Clears signatures if the representative role has changed.
 *
 * @param {Object} params The function parameters.
 * @param {Values} params.answers Current form answers.
 * @param {Values} params.values New form values.
 * @returns {Values} Updated form values with cleared signatures if needed.
 */
export function clearSignaturesIfRepresentativeDidChange({
  answers,
  values,
}: {
  answers: Values;
  values: Values;
}) {
  if (!shouldClearSignatures(answers, values)) {
    return values;
  }

  return {
    ...values,
    [MAIN_SIGNATURE]: "",
    [MAIN_SIGNATURE_DATE]: "",
  };
}

/**
 * Filters out PDF nodes and sections, keeping only resident experience content.
 * This is a static step that should be done once after the template is fetched.
 *
 * @param {ResidentExperienceTemplate} template The resident experience template to filter.
 * @returns {Template} Filtered template containing only resident experience content.
 */
export const extractResidentExperienceTemplate = (
  template: ResidentExperienceTemplate,
): Template => {
  const filterResidentExperienceNodes = (nodes: any) =>
    nodes
      .filter((node: any) => isResidentExperienceContextNode(node))
      .map((node: any) =>
        node.children
          ? {
              ...node,
              children: filterResidentExperienceNodes(node.children),
            }
          : node,
      );

  const pruneEmptyChildren = ({children, ...node}: any) => {
    if (!children) {
      return node;
    }

    const prunedChildren = children
      .map(pruneEmptyChildren)
      .filter((node: any) => !node.children || node.children.length > 0);

    return {
      ...node,
      children: prunedChildren,
    };
  };

  return pruneEmptyChildren({
    ...template,
    children: template.children.map(sectionGroup => ({
      ...sectionGroup,
      children: filterResidentExperienceNodes(sectionGroup.children),
    })),
  });
};

/**
 * Extracts and processes resident experience slides from a template.
 * Note: It's expected that the template no longer has PDF context nodes.
 *
 * @param {Object} params The function parameters.
 * @param {Template} params.template The template to extract slides from.
 * @param {Admission} params.instance The admission instance.
 * @param {Values} params.answers Form answers for visibility calculations.
 * @param {Partial<{readOnly: boolean}>} params.computed Computed properties.
 * @param {boolean} [params.computeChildrenBlocks=true] Whether to compute children blocks.
 * @returns {any[]} Array of processed slide objects with state information.
 */
export const extractResidentExperienceSlides = ({
  template,
  instance,
  answers,
  computed,
  computeChildrenBlocks = true,
}: {
  template: Template;
  instance: Admission;
  answers: Values;
  computed: Partial<{readOnly: boolean}>;
  computeChildrenBlocks: boolean;
}) => {
  const preflightedAnswers = getPreflightedAnswers(instance.answers);
  const slides: any[] = [];

  // Pre-index flags by slideId for O(1) lookup instead of O(n) .find()
  // Note: Only keep first flag per slideId to match original .find() behavior
  const flagsBySlideId = new Map<string, (typeof instance.flags)[number]>();
  for (const flag of instance.flags) {
    if (!flagsBySlideId.has(flag.slideId)) {
      flagsBySlideId.set(flag.slideId, flag);
    }
  }

  // Pre-index completedSlides as Set for O(1) lookup instead of O(n) .includes()
  const completedSlidesSet = new Set(instance.completedSlides);

  template.children.forEach((group, groupIndex, groups) => {
    const isLastGroup = groupIndex + 1 === groups.length;

    group.children.forEach((section, sectionIndex, sections) => {
      const isLastSection = sectionIndex + 1 === sections.length;
      const sectionIsVisible = blockIsVisible(answers, section);
      const sectionSlides = section.children.filter(isSlideNode);

      sectionSlides
        .filter(slide => !slide.afterAdmission)
        .forEach((slide, slideIndex, sectionSlides) => {
          const flag = flagsBySlideId.get(slide.id);
          const [firstChild] = slide.children;

          slides.push({
            ...slide,
            children: computeChildrenBlocks
              ? slide.children.map(node =>
                  computeChildrenBlock({
                    node,
                    answers,
                    template,
                    computed,
                    preflightedAnswers,
                  }),
                )
              : slide.children,
            sectionId: section.id,
            sectionTitle: section.title,
            state: {
              complete:
                completedSlidesSet.has(slide.id) ||
                isTableOfContentNode(firstChild),
              fullscreen:
                !!slide.backgroundUrl || isTableOfContentNode(firstChild),
              last:
                isLastGroup &&
                isLastSection &&
                slideIndex + 1 === sectionSlides.length,
              visible: sectionIsVisible && blockIsVisible(answers, slide),
              flag: flag ? flag.id : null,
            },
          });
        });
    });
  });

  return slides;
};

/**
 * Helper to read well-known answer IDs.
 * Use the following accessor functions, instead of reading raw answers.
 *
 * @param {string} answerId The answer ID to read.
 * @returns {Function} Function that takes an admission and returns the answer value.
 */
const readAnswerValue = (answerId: string) => (admission?: Admission) =>
  admission?.answers?.[answerId]?.value ?? "";

/**
 * Reads the facility name from admission answers.
 */
export const readFacilityNameAnswer = readAnswerValue("facilityName");

/**
 * Reads the facility organization name from admission answers.
 */
export const readFacilityOrganizationNameAnswer = readAnswerValue(
  "facilityOrganizationName",
);

/**
 * Reads the resident's first name from admission answers.
 */
export const readResidentFirstNameAnswer = readAnswerValue(RESIDENT_FIRST_NAME);

/**
 * Reads the resident's last name from admission answers.
 */
export const readResidentLastNameAnswer = readAnswerValue(RESIDENT_LAST_NAME);

/**
 * Reads the resident's full name from admission answers.
 *
 * @param {Admission} admission The admission to read from.
 * @returns {string} The resident's full name.
 */
export const readResidentNameAnswer = (admission: Admission) =>
  `${readResidentFirstNameAnswer(admission)} ${readResidentLastNameAnswer(
    admission,
  )}`;

/**
 * Reads the resident's email from admission answers.
 */
export const readResidentEmailAnswer = readAnswerValue("resident_email");

/**
 * Reads the resident's phone from admission answers.
 */
export const readResidentPhoneAnswer = readAnswerValue("resident_phone");

/**
 * Reads the date of admission from admission answers.
 */
export const readDateOfAdmissionAnswer = readAnswerValue(
  "preflight_dateOfAdmission",
);

/**
 * Reads the resident's PCC account status from admission answers.
 */
export const readResidentPccAccountStatus = readAnswerValue(
  RESIDENT_PCC_ACCOUNT_STATUS,
);
